import { bunja } from 'bunja';
import { atom } from 'jotai';
import { ReactNode } from 'react';

import { T } from '@flipster/i18n';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { TpslTypeScope, selectedTpslOrderBunja, tpslModalBunja } from '../state';
import { instrumentBunja, parentPositionBunja, resolveTriggerReferencePrice, tickerPriceBunja } from '../state/base';
import { tpslModalInputBunja, tpslModalInputDerivationBunja } from './state';
import {
  evaluatePriceCase,
  isTpslSubmitDisabled,
  resolveTriggerPriceMessageKind,
  resolveTriggerPriceStatus,
  type PriceInputCase,
  type ValidationStatus,
} from './tpsl-utils';

/** 트리거 가격 검증 상태. status에 따라 섹션에서 Error/Warning 메시지를, submit에서 차단 여부를 파생한다. */
type TriggerPriceStatus =
  | { status: Extract<ValidationStatus, 'valid'>; message?: undefined }
  | { status: Exclude<ValidationStatus, 'valid'>; message: ReactNode };

/** Stable identity so valid→valid ticks do not thrash Trigger / Confirm subscribers. */
const validTriggerPriceStatus = { status: 'valid' } as const satisfies TriggerPriceStatus;

export const tpslModalValidationBunja = bunja(() => {
  const tpslType = bunja.use(TpslTypeScope);
  const { lastPriceAtom, markPriceAtom: tickerMarkPriceAtom } = bunja.use(tickerPriceBunja);
  const { positionType, sideAtom, orderPriceAtom, liqPriceAtom, marginType } = bunja.use(parentPositionBunja);
  const { notionalMinOrderAmountAtom, positionUnitAtom, pricePrecisionAtom, tradeQtyPrecisionAtom, unitOrderQtyAtom } =
    bunja.use(instrumentBunja);
  const { positionQtyAtom, isEntireAtom } = bunja.use(tpslModalBunja);
  const { tpslOrderQtyAtom, selectedTpslOrder } = bunja.use(selectedTpslOrderBunja);
  const isUpdateMode = Boolean(selectedTpslOrder);
  const {
    modalIsDirtyAtom,
    qtyDirtyAtom,
    triggerPriceInputIsEmptyAtom,
    triggerPriceInputValueAtom,
    limitPriceInputIsEmptyAtom,
    isLimitTpslOrderAtom,
    isBboAtom,
    partialQtyAtom,
    triggerPriceTypeAtom,
  } = bunja.use(tpslModalInputBunja);
  const { isTotalLossAtom } = bunja.use(tpslModalInputDerivationBunja);

  const triggerReferencePriceAtom = atomWithBigNumber((get) =>
    resolveTriggerReferencePrice(get(triggerPriceTypeAtom), {
      lastPrice: get(lastPriceAtom),
      markPrice: get(tickerMarkPriceAtom),
    }),
  );

  const triggerPriceInputCaseAtom = atom<PriceInputCase>((get) =>
    evaluatePriceCase({
      tpslType,
      positionType,
      marginType,
      value: get(triggerPriceInputValueAtom),
      isEmpty: get(triggerPriceInputIsEmptyAtom),
      side: get(sideAtom),
      targetPrice: positionType === 'open' ? get(triggerReferencePriceAtom) : get(orderPriceAtom),
      liqPrice: Num(get(liqPriceAtom)),
    }),
  );

  // 메시지 종류(kind)는 resolveTriggerPriceMessageKind가 단일 결정원이고, 여기서는 종류별 i18n 문구만 매핑한다.
  const triggerPriceMessageKindAtom = atom((get) =>
    resolveTriggerPriceMessageKind({ inputCase: get(triggerPriceInputCaseAtom), isUpdateMode }),
  );

  const triggerPriceErrorMessageAtom = atom<ReactNode>((get) => {
    switch (get(triggerPriceMessageKindAtom)) {
      case 'above-market':
        return <T id='Trade.r250401.Price_above_market_required_error' />;
      case 'below-market':
        return <T id='Trade.r250401.Price_below_market_required_error' />;
      case 'above-order':
        return <T id='Trade.r250401.Price_above_order_required_error' />;
      case 'below-order':
        return <T id='Trade.r250401.Price_below_order_required_error' />;
      case 'liq-too-low':
      case 'liq-too-high': {
        const liquidationPrice = numberFormatter(String(get(liqPriceAtom)), {
          precision: get(pricePrecisionAtom),
          toFixed: true,
        });
        return (
          <T
            id={
              get(triggerPriceMessageKindAtom) === 'liq-too-low'
                ? 'Trade.r250401.Price_too_low_liquidation_error'
                : 'Trade.r250401.Price_too_high_liquidation_error'
            }
            values={{ liquidationPrice }}
          />
        );
      }
      default:
        return undefined;
    }
  });

  const triggerPriceWarningMessageAtom = atom<ReactNode>((get) =>
    get(triggerPriceMessageKindAtom) === 'immediate-close' ? (
      <T id='Trade.r260616.Tp_sl_immediate_close_warning' />
    ) : undefined,
  );

  // 트리거 가격 검증 상태를 valid | warning | error 한 값으로 모델링한다.
  // 메시지 렌더(섹션의 Error/Warning 분기)와 Confirm 활성(submitDisabled)을 모두 여기서 파생한다.
  const triggerPriceStatusAtom = atom<TriggerPriceStatus>((get) => {
    const status = resolveTriggerPriceStatus({ inputCase: get(triggerPriceInputCaseAtom), isUpdateMode });
    switch (status) {
      case 'warning':
        return { status, message: get(triggerPriceWarningMessageAtom) };
      case 'error':
        return { status, message: get(triggerPriceErrorMessageAtom) };
      case 'valid':
      default:
        return validTriggerPriceStatus;
    }
  });

  const hasTriggerPriceInputErrorAtom = atom((get) => get(triggerPriceStatusAtom).status === 'error');

  // 인라인 에러 메시지는 trigger price 에만 적용한다 (limit price 에는 노출하지 않는다).
  // (#prex-pod-trading-systems 결정: Don/Changyeon — Advanced·Order TP/SL 페이지 공통)
  // ref: https://prextech.slack.com/archives/C0AQX0CL77F/p1781510600692929?thread_ts=1781246831.641929&cid=C0AQX0CL77F
  //
  // 단, user-set Limit(Limit Order on, BBO off)에서 limit price 는 필수이므로 빈 입력은
  // `empty`로 두어 Confirm 만 막고(메시지는 없음), 방향(too-low/too-high) 검증은 하지 않는다.
  // Change/ROI/PnL 부호는 Confirm을 막지 않는다. 익절로스(진입가와 현재가 사이 SL)처럼
  // 시장가 룰은 통과하지만 secondary 가 기대 부호와 반대인 가격 입력을 허용한다.
  const limitPriceInputCaseAtom = atom<PriceInputCase>((get) => {
    if (!get(isLimitTpslOrderAtom)) return 'valid';
    if (get(isBboAtom)) return 'valid';
    if (get(limitPriceInputIsEmptyAtom)) return 'empty';
    return 'valid';
  });
  const hasLimitPriceInputErrorAtom = atom((get) => get(limitPriceInputCaseAtom) !== 'valid');
  // limit price 는 인라인 메시지를 노출하지 않는다(빈 입력은 hasLimitPriceInputError로 Confirm만 막음).
  const limitPriceInputErrorMessageAtom = atom<ReactNode>(() => undefined);

  const hasTpslAmountWarningAtom =
    positionType === 'pending'
      ? atom(false)
      : atom((get) => {
          const hasMinQtyError = get(hasMinQtyErrorAtom);
          const positionQty = get(positionQtyAtom);
          const tpslOrderQty = get(tpslOrderQtyAtom);
          if (hasMinQtyError) return false;
          if (!tpslOrderQty || !positionQty) return false;
          return Num(tpslOrderQty).isGreaterThan(positionQty);
        });

  const tpslAmountWarningMessageAtom = atom((get) => {
    const positionQty = get(positionQtyAtom);
    const positionUnit = get(positionUnitAtom);
    const tradeQtyPrecision = get(tradeQtyPrecisionAtom);
    const hasTpslAmountWarning = get(hasTpslAmountWarningAtom);
    if (!hasTpslAmountWarning) return;
    const tpOrSl = tpslType === 'tp' ? 'TP' : 'SL';
    const available = numberFormatter(String(positionQty), { precision: tradeQtyPrecision, toFixed: true });
    return [
      `Your ${tpOrSl} amount exceeds your current position.`,
      `${available} ${positionUnit} is available to execute.`,
    ].join('\n');
  });

  const minTpslOrderQtyAtom = atom((get) => {
    const unitOrderQty = get(unitOrderQtyAtom);
    const notionalMinOrderAmount = get(notionalMinOrderAmountAtom);
    const triggerPriceInputValue = get(triggerPriceInputValueAtom);
    return BigNumber.max(unitOrderQty, notionalMinOrderAmount.dividedBy(triggerPriceInputValue));
  });

  const hasMinQtyErrorAtom = atom((get) => {
    const isEntire = get(isEntireAtom);
    if (isEntire) return false;
    const triggerPriceInputIsEmpty = get(triggerPriceInputIsEmptyAtom);
    const minTpslOrderQty = get(minTpslOrderQtyAtom);
    const partialQty = Num(get(partialQtyAtom) || '0');
    if (triggerPriceInputIsEmpty) return false;
    return minTpslOrderQty.isGreaterThan(partialQty);
  });

  const partialTpslSliderErrorMessageAtom = atom<ReactNode>((get) => {
    const qtyDirty = get(qtyDirtyAtom);
    if (!qtyDirty) return null;
    const hasMinQtyError = get(hasMinQtyErrorAtom);
    const minTpslOrderQty = get(minTpslOrderQtyAtom);
    const positionUnit = get(positionUnitAtom);
    const tradeQtyPrecision = get(tradeQtyPrecisionAtom);
    if (!hasMinQtyError) return null;
    return (
      <T
        id='Trade.r250401.Min_quantity_error'
        values={{
          amount: numberFormatter(String(minTpslOrderQty), {
            method: 'round-up',
            precision: tradeQtyPrecision,
            toFixed: true,
          }),
          symbol: positionUnit,
        }}
      />
    );
  });

  const submitDisabledAtom = atom((get) =>
    isTpslSubmitDisabled({
      triggerPriceInputIsEmpty: get(triggerPriceInputIsEmptyAtom),
      hasTriggerPriceInputError: get(hasTriggerPriceInputErrorAtom),
      hasLimitPriceInputError: get(hasLimitPriceInputErrorAtom),
      hasTpslAmountWarning: get(hasTpslAmountWarningAtom),
      modalIsDirty: get(modalIsDirtyAtom),
      hasMinQtyError: get(hasMinQtyErrorAtom),
      isTotalLoss: get(isTotalLossAtom),
    }),
  );

  return {
    triggerPriceStatusAtom,
    limitPriceInputErrorMessageAtom,
    tpslAmountWarningMessageAtom,
    partialTpslSliderErrorMessageAtom,
    submitDisabledAtom,
  };
});
tpslModalValidationBunja.debugLabel = 'tpslModalValidationBunja';
